import Axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { UnauthenticatedUserHandler } from '~src/authentication/services/UnauthenticatedUserHandler';
import { ServiceLocator } from '../app/ServiceLocator';
import {
  ErrorReason,
  getSessionManagementServiceInstance
} from '../sessionManagement/SessionManagementService';
import { TagRequest } from '../ts/ServerApi/requests/TagRequest';
import { ITagResult } from './serverApi/types/IResponse';
import { ITagError } from './serverApi/types/ITagError';
import HttpStatusCode from './utils/HttpStatusCode';


/**
 * This library implements HTTP client for TAG based APIs.
 */

namespace TagHttpClient {

  function requestViaTag<TResult extends ITagResult>(requestConfig: AxiosRequestConfig): Promise<TResult> {
    return Axios(requestConfig).then(
      (successResponse: AxiosResponse<TResult>) => {
        try {
          ServiceLocator.get<ng.IRootScopeService>('$rootScope').$applyAsync();
        } catch {
          // ignore the error since it only happens in unit tests if $rootscope is not injected
        }
        return successResponse.data;
      },
      (errorObject: AxiosError<ITagError>) => {
        try {
          ServiceLocator.get<ng.IRootScopeService>('$rootScope').$applyAsync();
        } catch {
          // ignore the error since it only happens in unit tests if $rootscope is not injected
        }
        if (errorObject.response.status === HttpStatusCode.Unauthorized) {
          const reason: number = errorObject.response.data.httpErrorCode;
          UnauthenticatedUserHandler.handle401(reason);
          return Promise.reject(reason || ErrorReason.Unknown);
        } else {
          const error: ITagError = errorObject.response.data;
          const result: TResult = { errors: [error] } as TResult;
          return result;
        }
      }
    );
  }

  function requestPostViaTag<TParams, TResult extends ITagResult>(request: TagRequest<TParams, TResult>): Promise<TResult> {
    return requestViaTag<TResult>({
      method: 'POST',
      url: request.url(),
      data: request.data()
    });
  }

  /**
    * Make a server request using Tableau API gateway. The request is made `within` session, aka after getSessionInfo call finishes
    * @param {TagRequest} request - request object extending TagRequest
    * @returns {Promise} Promise that resolves with server response
    */
  export function sendTagRequest<TParams, TResult>(request: TagRequest<TParams, TResult>): Promise<TResult> {
    return getSessionManagementServiceInstance().sendRequestWithinSession(() => requestPostViaTag(request));
  }
}

export default TagHttpClient;
